import { resolvePipelineStage, resolveStageLabel } from '../../utils/workspaceCaseStage'

/** Shared pipeline stage + transition rules (Investigation Pipeline + Investigation Case). */

export const STAGE_TO_STATUS = {
  investigation: 'INVESTIGATING',
  pending_evidence: 'PENDING_EVIDENCE',
  suspicion_formed: 'SUSPICION_FORMED',
  smr_draft: 'SMR_DRAFT',
  under_review: 'PENDING_APPROVAL',
  filed: 'FILED',
  closed: 'CLOSED',
}

export const STAGES = [
  { id: 'investigation', label: 'Investigation' },
  { id: 'pending_evidence', label: 'Pending Evidence' },
  { id: 'suspicion_formed', label: 'Suspicion Formed' },
  { id: 'smr_draft', label: 'SMR Draft' },
  { id: 'under_review', label: 'SMR Approved' },
  { id: 'filed', label: 'SMR Filed' },
  { id: 'closed', label: 'Closed' },
]

/** SVCP §9 suspicion-formation acknowledgments — persist the full sentence on the audit row. */
export const SUSPICION_BASIS_ACKS = [
  { id: 'facts_documented', label: 'I have documented the specific facts I observed.' },
  { id: 'innocent_explanation_considered', label: 'I have considered and documented an innocent explanation.' },
  { id: 'innocent_explanation_ruled_out', label: 'I have documented why that innocent explanation is ruled out.' },
  { id: 'reasonable_grounds_objective_test', label: 'An objective person with this information would also form suspicion — these are reasonable grounds, not mere suspicion.' },
]

export function collectTransitionAcknowledgements(rule, checked = {}, suspicionAcks = {}) {
  const items = []
  for (const cond of rule?.conditions || []) {
    if (!checked[cond.id]) continue
    items.push({ id: cond.id, label: cond.label, checked: true })
  }
  if (rule?.to === 'suspicion_formed') {
    for (const ack of SUSPICION_BASIS_ACKS) {
      if (!suspicionAcks[ack.id]) continue
      items.push({ id: ack.id, label: ack.label, checked: true })
    }
  }
  return items
}

export const TRANSITION_RULES = {
  investigation: {
    forward: [
      { to: 'pending_evidence', label: 'Pause — Pending Evidence', allowedRoles: ['co', 'manager'], conditions: [{ id: 'evidence_identified', label: 'I have identified what evidence is outstanding' }, { id: 'request_sent', label: 'I have sent the request or notified the relevant party' }, { id: 'deadline_set', label: 'I have set a follow-up deadline' }], requiresReason: true, reasonLabel: 'What are you waiting for?', warning: false },
      { to: 'suspicion_formed', label: 'Form Suspicion — Start SMR Clock', allowedRoles: ['co', 'manager'], conditions: [{ id: 'evidence_assessed', label: 'I have reviewed all available evidence' }, { id: 'innocent_considered', label: 'I have considered and ruled out innocent explanations' }, { id: 'grounds_documented', label: 'I have documented the grounds for suspicion in the investigation notes' }], requiresReason: true, reasonLabel: 'Basis for suspicion', warning: true, warningText: 'Legal event. SMR clock starts (3 business days). This record is permanent and cannot be deleted.' },
      { to: 'closed', label: 'Close — No Further Action', allowedRoles: ['co'], needsManagerApproval: true, conditions: [{ id: 'investigation_complete', label: 'I have completed and documented the investigation' }, { id: 'innocent_explanation', label: 'I have documented the innocent explanation' }], requiresReason: true, reasonLabel: 'Document the innocent explanation', warning: false },
    ],
    backward: [],
  },
  pending_evidence: {
    forward: [
      { to: 'investigation', label: 'Evidence Received — Resume', allowedRoles: ['co', 'manager'], conditions: [{ id: 'evidence_received', label: 'I confirm the requested evidence has been received' }], requiresReason: true, reasonLabel: 'What evidence was received?', warning: false },
      { to: 'suspicion_formed', label: 'Non-Response — Form Suspicion', allowedRoles: ['co', 'manager'], conditions: [{ id: 'timeout_elapsed', label: 'I confirm the deadline has passed without response' }, { id: 'grounds_documented', label: 'I have documented the grounds for suspicion in the investigation notes' }], requiresReason: true, reasonLabel: 'Basis for suspicion', warning: true, warningText: 'Legal event. SMR clock starts (3 business days).', direction: 'forward' },
    ],
    backward: [{ to: 'investigation', label: 'Return to Investigation', allowedRoles: ['manager'], conditions: [], requiresReason: true, reasonLabel: 'Reason for return', warning: true, warningText: 'Manager override.', direction: 'backward' }],
  },
  suspicion_formed: {
    forward: [{ to: 'smr_draft', label: 'Open SMR Draft', allowedRoles: ['co', 'manager'], conditions: [{ id: 'suspicion_documented', label: 'Suspicion grounds are recorded in the investigation record' }], requiresReason: false, direction: 'forward' }],
    backward: [{ to: 'investigation', label: 'Return to Investigation (Override)', allowedRoles: ['manager'], conditions: [{ id: 'legal_risk_noted', label: 'I acknowledge this is a high-risk override with legal implications' }], requiresReason: true, reasonLabel: 'Why reversing suspicion?', warning: true, warningText: 'CRITICAL. Reversing suspicion has legal implications. The SMR clock does not reset.', direction: 'backward' }],
  },
  smr_draft: {
    forward: [{ to: 'under_review', label: 'Submit for Manager Review', allowedRoles: ['co'], conditions: [{ id: 'narrative_complete', label: 'I have completed the SMR narrative' }, { id: 'evidence_referenced', label: 'I have referenced the supporting evidence in the narrative' }], requiresReason: true, reasonLabel: 'Note to manager', warning: false, direction: 'forward' }],
    backward: [{ to: 'suspicion_formed', label: 'Return to Suspicion Formed', allowedRoles: ['manager'], conditions: [], requiresReason: true, reasonLabel: 'Reason for return', warning: true, warningText: 'SMR clock still running.', direction: 'backward' }],
  },
  under_review: {
    forward: [{ to: 'filed', label: 'Approve and Submit to AUSTRAC', allowedRoles: ['manager'], conditions: [{ id: 'narrative_reviewed', label: 'I have reviewed the full SMR narrative' }, { id: 'evidence_confirmed', label: 'I have confirmed the evidence supports the filing' }], requiresReason: true, reasonLabel: 'Manager approval note', warning: false, direction: 'forward' }],
    backward: [{ to: 'smr_draft', label: 'Request Revision from CO', allowedRoles: ['manager'], conditions: [], requiresReason: true, reasonLabel: 'What needs revision?', warning: true, warningText: 'Revision consumes SMR clock time.', direction: 'backward' }],
  },
  filed: { forward: [], backward: [] },
  closed: { forward: [], backward: [] },
}

export function ruleChangesApiStatus(rule, currentApiStatus) {
  const target = STAGE_TO_STATUS[rule.to]
  if (!target) return true
  return String(target).toUpperCase() !== String(currentApiStatus || '').toUpperCase()
}

export function resolveCurrentUserMember(teamMembers, authUser) {
  if (!authUser) return null
  const uname = String(authUser.username || authUser.sub || '').trim().toLowerCase()
  if (!uname) return null
  return teamMembers.find((m) => String(m.username || '').trim().toLowerCase() === uname) || null
}

export function hasPipelineViewPermission(can) {
  return can('compliance-safe/investigations/pipeline', 'view')
    || can('compliance-safe/investigations', 'view')
    || can('compliance-safe/cases', 'view')
    || can('cases', 'view')
}

export function hasPipelineManagePermission(can) {
  return can('compliance-safe/investigations/pipeline', 'manage')
    || can('compliance-safe/investigations', 'manage')
    || can('compliance-safe/cases', 'manage')
    || can('cases', 'manage')
}

export function canUserTransitionCase(caseData, teamMembers, authUser, hasEditPermission, hasManagePipeline) {
  if (!hasEditPermission) return false
  if (hasManagePipeline) return true
  const assignedUserId = caseData?.assignee?.userId
  if (assignedUserId == null) return true
  const me = resolveCurrentUserMember(teamMembers, authUser)
  return me?.userId != null && Number(me.userId) === Number(assignedUserId)
}

/** Days in current pipeline stage before UI highlights as stale. */
export const STAGE_STALE_DAYS = 7

/** Typical venue CCTV retention from incident date (footage may be overwritten after this). */
export const CCTV_RETENTION_DAYS = 21

/** ±30 minutes around a known event time. */
export const CCTV_REVIEW_PAD_MS = 30 * 60 * 1000

const MS_PER_DAY = 86400000

function parseDate(raw) {
  if (raw == null || raw === '') return null
  const d = raw instanceof Date ? raw : new Date(raw)
  return Number.isNaN(d.getTime()) ? null : d
}

/** True when the source string is a calendar date without a meaningful time of day. */
export function isDateOnlyTimeValue(raw) {
  if (raw == null || raw === '') return false
  const s = String(raw).trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return true
  if (/^\d{4}-\d{2}-\d{2}[ T]00:00:00(\.0+)?(Z|[+-]\d{2}:?\d{2})?$/.test(s)) return true
  return false
}

function hasMeaningfulTimeOfDay(raw, d) {
  if (!d) return false
  if (isDateOnlyTimeValue(raw)) return false
  const s = String(raw || '').trim()
  if (/\d{1,2}:\d{2}/.test(s) && !/T00:00| 00:00/.test(s)) return true
  return d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0
}

function startOfLocalDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
}

function endOfLocalDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
}

function formatDateForDatetimeLocalInput(d) {
  if (!d || Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const h = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${day}T${h}:${min}`
}

function detectCctvEvidenceSource(caseRow, snap) {
  if (!snap || typeof snap !== 'object') return 'generic'
  if (snap.payout_id || snap.source === 'payout' || caseRow?.rule_id === 'PAYOUT_ESCALATION') return 'payout'
  if (snap.incident_id || snap.source === 'incidents' || caseRow?.rule_id === 'INCIDENT_UAR') return 'incident'
  if (caseRow?.source_alert_id || snap.TransactionPK || snap.transaction_id) return 'alert'
  return 'generic'
}

function firstPresent(snap, keys, { skipRaw } = {}) {
  for (const k of keys) {
    const v = snap[k]
    if (v == null || String(v).trim() === '') continue
    if (skipRaw && skipRaw(v)) continue
    return { key: k, raw: v, date: parseDate(v) }
  }
  return null
}

function isLikelyCaseOpenedBackfill(raw, caseRow) {
  const d = parseDate(raw)
  const opened = parseDate(caseRow?.created_at)
  if (!d || !opened) return false
  return Math.abs(d.getTime() - opened.getTime()) < 120000
}

/**
 * Source-aware incident anchor for CCTV retention + review windows.
 * Alerts → transaction time; payouts → slip/issue date; UAR → report time.
 */
export function resolveCctvIncidentAnchor(caseRow, snapshot) {
  const snap = snapshot && typeof snapshot === 'object' ? snapshot : {}
  const source = detectCctvEvidenceSource(caseRow, snap)

  if (source === 'payout') {
    const hit = firstPresent(snap, [
      'slip_date', 'SlipDate', 'issue_date', 'IssueDate', 'slip_issue_date',
      'timestamp', 'recorded_at', 'RecordedAt',
    ])
    if (hit?.date) {
      return {
        source,
        anchorRaw: hit.raw,
        anchorDate: hit.date,
        anchorLabel: hit.key.includes('slip') || hit.key.includes('Slip') || hit.key.includes('issue') || hit.key.includes('Issue')
          ? 'Slip / issue date'
          : 'Payout recorded in system',
        hasTime: hasMeaningfulTimeOfDay(hit.raw, hit.date),
      }
    }
  }

  if (source === 'incident') {
    const hit = firstPresent(snap, [
      'incident_reported_at', 'reported_at', 'ReportedAt',
      'SnapShotTime', 'snapshot_time', 'timestamp', 'created_at',
    ])
    if (hit?.date) {
      return {
        source,
        anchorRaw: hit.raw,
        anchorDate: hit.date,
        anchorLabel: 'UAR reported',
        hasTime: hasMeaningfulTimeOfDay(hit.raw, hit.date),
      }
    }
  }

  const sessionStart = parseDate(snap.StartTime || snap.start_time)
  const sessionEnd = parseDate(snap.EndTime || snap.end_time)
  if (sessionStart && sessionEnd && sessionEnd.getTime() >= sessionStart.getTime()) {
    const hasSessionTime =
      hasMeaningfulTimeOfDay(snap.StartTime || snap.start_time, sessionStart) ||
      hasMeaningfulTimeOfDay(snap.EndTime || snap.end_time, sessionEnd)
    return {
      source: 'session',
      anchorRaw: snap.StartTime || snap.start_time,
      anchorDate: sessionStart,
      anchorEndDate: sessionEnd,
      anchorLabel: 'Gaming session',
      hasTime: hasSessionTime,
      sessionMode: true,
    }
  }

  const hit = firstPresent(snap, [
    'SnapShotTime', 'snapshot_time', 'TransactionTime', 'transaction_time',
    'EventTime', 'event_time', 'timestamp',
  ], { skipRaw: (raw) => isLikelyCaseOpenedBackfill(raw, caseRow) })
  if (hit?.date) {
    return {
      source: source === 'alert' ? 'alert' : 'generic',
      anchorRaw: hit.raw,
      anchorDate: hit.date,
      anchorLabel: source === 'alert' ? 'Transaction time' : 'Event time',
      hasTime: hasMeaningfulTimeOfDay(hit.raw, hit.date),
    }
  }

  const caseOpened = parseDate(caseRow?.created_at)
  if (caseOpened) {
    return {
      source: 'case_opened',
      anchorRaw: caseRow.created_at,
      anchorDate: caseOpened,
      anchorLabel: 'Case opened (event time unknown)',
      hasTime: hasMeaningfulTimeOfDay(caseRow.created_at, caseOpened),
      usedCaseOpenedFallback: true,
    }
  }

  return null
}

function buildCctvReviewWindow(anchorInfo) {
  if (!anchorInfo?.anchorDate) return null

  if (anchorInfo.sessionMode && anchorInfo.anchorEndDate) {
    const start = anchorInfo.anchorDate
    const end = anchorInfo.anchorEndDate
    if (anchorInfo.hasTime) {
      return {
        mode: 'session',
        reviewStart: new Date(start.getTime() - CCTV_REVIEW_PAD_MS),
        reviewEnd: new Date(end.getTime() + CCTV_REVIEW_PAD_MS),
        sessionSpanStart: start,
        sessionSpanEnd: end,
        windowLabel: 'Suggested CCTV review window (±30 min around session)',
      }
    }
    const dayStart = startOfLocalDay(start)
    const dayEnd = endOfLocalDay(end)
    return {
      mode: 'session_full_day',
      reviewStart: dayStart,
      reviewEnd: dayEnd,
      sessionSpanStart: start,
      sessionSpanEnd: end,
      windowLabel: 'Suggested CCTV review window (full calendar day — session time unknown)',
    }
  }

  const point = anchorInfo.anchorDate
  if (anchorInfo.hasTime) {
    return {
      mode: 'point',
      reviewStart: new Date(point.getTime() - CCTV_REVIEW_PAD_MS),
      reviewEnd: new Date(point.getTime() + CCTV_REVIEW_PAD_MS),
      windowLabel: `Suggested CCTV review window (±30 min around ${anchorInfo.anchorLabel.toLowerCase()})`,
    }
  }

  const dayStart = startOfLocalDay(point)
  const dayEnd = endOfLocalDay(point)
  return {
    mode: 'full_day',
    reviewStart: dayStart,
    reviewEnd: dayEnd,
    windowLabel: `Suggested CCTV review window (full calendar day — no time on ${anchorInfo.anchorLabel.toLowerCase()})`,
  }
}

/**
 * Venue / EGM + CCTV review timing for investigation Evidence tab.
 */
export function resolveCctvEvidenceTiming(caseRow, snapshot, { sourceDataId } = {}) {
  const snap = snapshot && typeof snapshot === 'object' ? snapshot : {}
  const empty = {
    venue: null,
    egms: [],
    from: '',
    to: '',
    mode: null,
    sessionSpanStartIso: null,
    sessionSpanEndIso: null,
    transactionAnchorIso: null,
    anchorLabel: null,
    windowLabel: null,
    usedCaseOpenedFallback: false,
  }

  const venue = snap.Venue || snap.venue || null
  const egmSingle =
    snap.EgmHouseNo || snap.egm_house_no || snap.EgmName || snap.egm_name || snap.egm_id || snap.game_id
    || (sourceDataId && /EGM|egm/i.test(sourceDataId) ? sourceDataId : null)
  const egms = egmSingle
    ? [egmSingle]
    : (Array.isArray(snap.egms) ? snap.egms : (snap.Egms ? (Array.isArray(snap.Egms) ? snap.Egms : [snap.Egms]) : []))

  const anchorInfo = resolveCctvIncidentAnchor(caseRow, snap)
  if (!anchorInfo) {
    return { ...empty, venue, egms: egms.filter(Boolean) }
  }

  const window = buildCctvReviewWindow(anchorInfo)
  if (!window) {
    return { ...empty, venue, egms: egms.filter(Boolean) }
  }

  const transactionAnchorIso =
    anchorInfo.sessionMode
      ? null
      : anchorInfo.anchorDate.toISOString()

  return {
    venue,
    egms: egms.filter(Boolean),
    from: formatDateForDatetimeLocalInput(window.reviewStart),
    to: formatDateForDatetimeLocalInput(window.reviewEnd),
    mode: window.mode,
    sessionSpanStartIso: window.sessionSpanStart ? window.sessionSpanStart.toISOString() : null,
    sessionSpanEndIso: window.sessionSpanEnd ? window.sessionSpanEnd.toISOString() : null,
    transactionAnchorIso,
    anchorLabel: anchorInfo.anchorLabel,
    windowLabel: window.windowLabel,
    usedCaseOpenedFallback: Boolean(anchorInfo.usedCaseOpenedFallback),
  }
}

/** Earliest incident / event time for CCTV retention (source-aware anchor). */
export function pickIncidentDate(caseRow, snapshot) {
  const anchor = resolveCctvIncidentAnchor(caseRow, snapshot)
  if (anchor?.anchorDate) return anchor.anchorDate
  return parseDate(caseRow?.created_at)
}

/**
 * Timestamp for "how long in this column" — avoids using generic updated_at for SMR filed/closed.
 */
export function pickStageEnteredAt(apiCase, stage, status) {
  if (!apiCase) return null
  const st = (status || apiCase.status || 'INVESTIGATING').toUpperCase()
  const stageId = stage || resolvePipelineStage(apiCase)
  if (stageId === 'filed' || st === 'FILED') {
    return apiCase.resolved_at || apiCase.regulatory_report_date || apiCase.updated_at || apiCase.created_at
  }
  if (stageId === 'under_review' && st === 'PENDING_APPROVAL') {
    return apiCase.updated_at || apiCase.created_at
  }
  if (stageId === 'closed' && (st === 'RESOLVED' || st === 'CLOSED')) {
    return apiCase.closed_at || apiCase.resolved_at || apiCase.updated_at || apiCase.created_at
  }
  return apiCase.updated_at || apiCase.created_at
}

export function daysSince(date) {
  const d = parseDate(date)
  if (!d) return null
  return Math.floor((Date.now() - d.getTime()) / MS_PER_DAY)
}

/** True when case has been in the current (non-terminal) stage longer than STAGE_STALE_DAYS. */
export function getStageStaleInfo(stageEnteredAt, stageId) {
  if (['filed', 'closed'].includes(String(stageId || ''))) return null
  const daysInStage = daysSince(stageEnteredAt)
  if (daysInStage == null || daysInStage <= STAGE_STALE_DAYS) return null
  return {
    daysInStage,
    daysOverThreshold: daysInStage - STAGE_STALE_DAYS,
  }
}

/**
 * Human label for time in the current pipeline stage (not overall case age).
 * @param {'detail'|'pipeline'} context — detail pages name the stage; pipeline board columns already imply it.
 */
export function formatDaysInStageLabel(daysInStage, stageLabel, { context = 'pipeline' } = {}) {
  if (daysInStage == null) return ''
  if (context === 'detail' && stageLabel) return `${daysInStage}d in ${stageLabel}`
  return `${daysInStage}d in stage`
}

export function stageAgingTooltip(stageLabel, stageEnteredAt) {
  const days = daysSince(stageEnteredAt)
  const dayPart = days != null ? `${days} day(s)` : 'Time'
  const stagePart = stageLabel ? ` in “${stageLabel}”` : ' in this pipeline stage'
  return `${dayPart}${stagePart} — not overall case age (see “Opened … ago” in the header).`
}

export function getCctvRetentionStatus(incidentDate, retentionDays = CCTV_RETENTION_DAYS) {
  const incident = parseDate(incidentDate)
  if (!incident) return null
  const expiryMs = incident.getTime() + retentionDays * MS_PER_DAY
  const daysLeft = Math.ceil((expiryMs - Date.now()) / MS_PER_DAY)
  return {
    incidentDate: incident,
    retentionDays,
    daysLeft,
    overdue: daysLeft <= 0,
    urgent: daysLeft > 0 && daysLeft <= 7,
  }
}

export function buildWorkflowCaseFromApi(apiCase, teamMembers = []) {
  const stage = resolvePipelineStage(apiCase)
  let assigneeMember = null
  if (apiCase.assigned_staff_id != null) {
    assigneeMember = teamMembers.find((m) => m.staffId === Number(apiCase.assigned_staff_id))
  }
  if (!assigneeMember && apiCase.assigned_user_id != null) {
    assigneeMember = teamMembers.find((m) => m.userId === Number(apiCase.assigned_user_id))
  }
  return {
    stage,
    stageLabel: resolveStageLabel(stage),
    _apiStatus: String(apiCase.status || 'INVESTIGATING').toUpperCase(),
    _dbId: apiCase.id,
    id: apiCase.case_number || apiCase.id,
    assignee: {
      userId: apiCase.assigned_user_id != null ? Number(apiCase.assigned_user_id) : null,
      name: assigneeMember?.name || apiCase.assigned_to || 'Unassigned',
    },
  }
}
